import React, { useState, useEffect } from 'react';
import { adminService } from '../services/adminService';
import UserManagement from './UserManagement';
import './AdminDashboard.css';

const AdminDashboard = ({ user, addNotification }) => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [activePeriod, setActivePeriod] = useState('month');
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [debugInfo, setDebugInfo] = useState(null);
    const [dashboardData, setDashboardData] = useState({
        stats: {
            totalBookings: 0,
            activeMembers: 0,
            pendingApprovals: 0,
            pendingItems: 0,
            facilityUtilization: 0,
            upcomingEvents: 0
        },
        bookingTrends: [],
        popularFacilities: [],
        statusDistribution: {},
        recentBookings: [],
        equipmentInventory: []
    });

    // Fetch all dashboard data
    const fetchDashboardData = async () => {
        setLoading(true);
        
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Request timeout after 10 seconds')), 10000);
        });
        
        try {
            console.log('🔍 Fetching dashboard data...');
            
            const bookingsPromise = adminService.getAllBookings();
            const usersPromise = adminService.getAllUsers();
            const pendingMembersPromise = adminService.getPendingMembers();
            
            const results = await Promise.race([
                Promise.allSettled([bookingsPromise, usersPromise, pendingMembersPromise]),
                timeoutPromise
            ]);
            
            if (results === undefined || (results.message && results.message.includes('timeout'))) {
                console.error('❌ Request timeout');
                setDashboardData({
                    stats: {
                        totalBookings: 0,
                        activeMembers: 0,
                        pendingApprovals: 0,
                        pendingItems: 0,
                        facilityUtilization: 0,
                        upcomingEvents: 0
                    },
                    bookingTrends: [],
                    popularFacilities: [],
                    statusDistribution: { pending: 0, approved: 0, rejected: 0, cancelled: 0 },
                    recentBookings: [],
                    equipmentInventory: []
                });
                if (addNotification) addNotification('Request timeout - showing partial data', 'warning');
                setLoading(false);
                return;
            }
            
            const bookingsRes = results[0]?.status === 'fulfilled' ? results[0]?.value : { success: false, bookings: [] };
            const usersRes = results[1]?.status === 'fulfilled' ? results[1]?.value : { success: false, users: [] };
            const pendingMembersRes = results[2]?.status === 'fulfilled' ? results[2]?.value : { success: false, users: [] };
            
            if (usersRes.success && usersRes.users) {
                setUsers(usersRes.users);
                console.log('✅ Users loaded:', usersRes.users.length);
            }
            
            const allBookings = bookingsRes?.bookings || [];
            const allUsers = usersRes?.users || [];
            const pendingMembers = pendingMembersRes?.users || [];
            
            const totalBookings = allBookings.length;
            const activeMembers = allUsers.filter(u => u.status === 'active').length;
            const pendingApprovals = pendingMembers.length;
            const pendingBookings = allBookings.filter(b => b.status === 'pending').length;
            const approvedBookings = allBookings.filter(b => b.status === 'approved').length;
            const rejectedBookings = allBookings.filter(b => b.status === 'rejected').length;
            const cancelledBookings = allBookings.filter(b => b.status === 'cancelled').length;
            
            const statusDistribution = {
                pending: pendingBookings,
                approved: approvedBookings,
                rejected: rejectedBookings,
                cancelled: cancelledBookings
            };
            
            const recentBookings = [...allBookings]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 6);
            
            const facilityCounts = {};
            allBookings.forEach(booking => {
                const facility = booking.facility_name || booking.location;
                if (facility) {
                    facilityCounts[facility] = (facilityCounts[facility] || 0) + 1;
                }
            });
            const popularFacilities = Object.entries(facilityCounts)
                .map(([name, count]) => ({ facility_name: name, booking_count: count }))
                .sort((a, b) => b.booking_count - a.booking_count)
                .slice(0, 5);
            
            let trendsData = [];
            
            if (activePeriod === 'week') {
                for (let i = 6; i >= 0; i--) {
                    const date = new Date();
                    date.setDate(date.getDate() - i);
                    const dateStr = date.toISOString().split('T')[0];
                    const count = allBookings.filter(b => b.created_at?.split(' ')[0] === dateStr).length;
                    trendsData.push({ 
                        date: dateStr, 
                        label: date.toLocaleDateString('en-US', { weekday: 'short' }),
                        total: count 
                    });
                }
            } else if (activePeriod === 'month') {
                for (let i = 29; i >= 0; i--) {
                    const date = new Date();
                    date.setDate(date.getDate() - i);
                    const dateStr = date.toISOString().split('T')[0];
                    const count = allBookings.filter(b => b.created_at?.split(' ')[0] === dateStr).length;
                    if (i % 5 === 0 || i === 0) {
                        trendsData.push({ 
                            date: dateStr, 
                            label: date.getDate().toString(),
                            total: count 
                        });
                    }
                }
            } else if (activePeriod === 'quarter') {
                const weeklyData = {};
                for (let i = 89; i >= 0; i--) {
                    const date = new Date();
                    date.setDate(date.getDate() - i);
                    const weekNum = Math.floor((date.getDate() - 1) / 7) + 1;
                    const weekKey = `Week ${weekNum}`;
                    const count = allBookings.filter(b => b.created_at?.split(' ')[0] === date.toISOString().split('T')[0]).length;
                    weeklyData[weekKey] = (weeklyData[weekKey] || 0) + count;
                }
                trendsData = Object.entries(weeklyData).map(([label, total]) => ({ label, total }));
            } else if (activePeriod === 'year') {
                const monthlyData = {};
                for (let i = 11; i >= 0; i--) {
                    const date = new Date();
                    date.setMonth(date.getMonth() - i);
                    const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
                    const count = allBookings.filter(b => {
                        const bookingDate = new Date(b.created_at);
                        return bookingDate.getMonth() === date.getMonth() && 
                               bookingDate.getFullYear() === date.getFullYear();
                    }).length;
                    monthlyData[monthKey] = (monthlyData[monthKey] || 0) + count;
                }
                trendsData = Object.entries(monthlyData).map(([label, total]) => ({ label, total }));
            }
            
            const newData = {
                stats: {
                    totalBookings,
                    activeMembers,
                    pendingApprovals,
                    pendingItems: pendingBookings,
                    facilityUtilization: totalBookings > 0 ? Math.round((approvedBookings / totalBookings) * 100) : 0,
                    upcomingEvents: 0
                },
                bookingTrends: trendsData,
                popularFacilities,
                statusDistribution,
                recentBookings,
                equipmentInventory: []
            };
            
            setDashboardData(newData);
            
            if (addNotification) addNotification('Dashboard loaded successfully', 'success');
            
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            
            setDashboardData({
                stats: {
                    totalBookings: 0,
                    activeMembers: 0,
                    pendingApprovals: 0,
                    pendingItems: 0,
                    facilityUtilization: 0,
                    upcomingEvents: 0
                },
                bookingTrends: [],
                popularFacilities: [],
                statusDistribution: { pending: 0, approved: 0, rejected: 0, cancelled: 0 },
                recentBookings: [],
                equipmentInventory: []
            });
            
            if (addNotification) addNotification('Failed to load dashboard data: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, [activePeriod]);

    // ★★★★★ ROLE MAPPING - Convert frontend role IDs to database values ★★★★★
    const getDatabaseRole = (frontendRoleId) => {
        const roleMapping = {
            // Executive Committee -> admin
            'president': 'admin',
            'vicePresident': 'admin',
            'secretary': 'admin',
            'treasurer': 'admin',
            'admin': 'admin',  // Super Admin
            
            // Committee Members -> moderator
            'sportsMember': 'moderator',
            'culturalMember': 'moderator',
            'technicalMember': 'moderator',
            'socialMember': 'moderator',
            'generalMember': 'moderator',
            
            // Staff
            'clubManager': 'admin',
            'clubAdministrator': 'moderator',
            
            // Regular -> member
            'member': 'member'
        };
        
        return roleMapping[frontendRoleId] || 'member';
    };

    // ★★★★★ USER MANAGEMENT FUNCTIONS ★★★★★
    const handleAssignRole = async (userId, newRole) => {
        console.log('=== ASSIGNING ROLE ===');
        console.log('User ID:', userId);
        console.log('Frontend Role:', newRole);
        
        // Map the frontend role to database value
        const dbRole = getDatabaseRole(newRole);
        console.log('Database Role (mapped):', dbRole);
        
        if (!userId) {
            console.error('No user ID provided');
            if (addNotification) addNotification('Error: No user selected', 'error');
            return;
        }
        
        try {
            console.log('Fetching user data for ID:', userId);
            const userResult = await adminService.getUserById(userId);
            console.log('User result:', userResult);
            
            if (!userResult.success || !userResult.user) {
                console.error('User not found:', userResult);
                if (addNotification) addNotification('User not found', 'error');
                return;
            }
            
            const user = userResult.user;
            console.log('Current role:', user.account_type);
            console.log('New role (DB):', dbRole);
            
            const updateData = {
                user_id: userId,
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                phone: user.phone || '',
                account_type: dbRole,  // ← Use the mapped database value
                reason: `Role: ${newRole} (DB: ${dbRole}) assigned by admin`
            };
            console.log('Update payload:', updateData);
            
            const updateResult = await adminService.updateUser(userId, updateData);
            console.log('Update result:', updateResult);
            
            if (updateResult.success) {
                const msg = `✅ Role updated to "${newRole}" successfully!`;
                console.log(msg);
                if (addNotification) addNotification(msg, 'success');
                await fetchDashboardData();
            } else {
                const msg = '❌ Failed to update role: ' + (updateResult.message || 'Unknown error');
                console.error(msg);
                if (addNotification) addNotification(msg, 'error');
            }
        } catch (error) {
            console.error('Assign role error:', error);
            if (addNotification) addNotification('❌ Error assigning role: ' + (error.message || 'Unknown error'), 'error');
        }
    };

    const handleUpdateUser = async (userId) => {
        console.log('=== VIEW USER ===');
        console.log('User ID:', userId);
        
        try {
            const result = await adminService.getUserById(userId);
            console.log('User data:', result);
            
            if (result.success && result.user) {
                const user = result.user;
                const userDetails = `👤 User Details:
━━━━━━━━━━━━━━━━━━━━━━━
Name: ${user.first_name || 'N/A'} ${user.last_name || 'N/A'}
Email: ${user.email || 'N/A'}
Phone: ${user.phone || 'N/A'}
Role: ${user.account_type || 'N/A'}
Status: ${user.status || 'N/A'}
Member #: ${user.membership_number || 'N/A'}
Joined: ${user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
━━━━━━━━━━━━━━━━━━━━━━━`;
                
                if (addNotification) {
                    addNotification(userDetails, 'info');
                } else {
                    alert(userDetails);
                }
            } else {
                const msg = 'User not found';
                console.error(msg);
                if (addNotification) addNotification(msg, 'error');
            }
        } catch (error) {
            console.error('Error getting user:', error);
            if (addNotification) addNotification('Error loading user details: ' + error.message, 'error');
        }
    };

    const handleActivateUser = async (userId) => {
        try {
            const result = await adminService.activateUser(userId);
            if (result.success) {
                if (addNotification) addNotification('✅ User activated successfully!', 'success');
                await fetchDashboardData();
            } else {
                if (addNotification) addNotification('❌ Activation failed: ' + result.message, 'error');
            }
        } catch (error) {
            console.error('Activate error:', error);
            if (addNotification) addNotification('Error activating user', 'error');
        }
    };

    const handleSuspendUser = async (userId, reason) => {
        try {
            const result = await adminService.suspendUser(userId, reason);
            if (result.success) {
                if (addNotification) addNotification('✅ User suspended successfully!', 'success');
                await fetchDashboardData();
            } else {
                if (addNotification) addNotification('❌ Suspension failed: ' + result.message, 'error');
            }
        } catch (error) {
            console.error('Suspend error:', error);
            if (addNotification) addNotification('Error suspending user', 'error');
        }
    };

    const handleDeleteUser = async (userId) => {
        try {
            const result = await adminService.deleteUser(userId);
            if (result.success) {
                if (addNotification) addNotification('✅ User deleted successfully!', 'success');
                await fetchDashboardData();
            } else {
                if (addNotification) addNotification('❌ Deletion failed: ' + result.message, 'error');
            }
        } catch (error) {
            console.error('Delete error:', error);
            if (addNotification) addNotification('Error deleting user', 'error');
        }
    };

    const testDirectApiCall = async () => {
        console.log('=== TESTING DIRECT API CALL ===');
        const userId = 152;
        const token = localStorage.getItem('nlngra_token');
        
        try {
            console.log('Testing GET...');
            const getResponse = await fetch(`/nlnapi/users.php?action=get&id=${userId}`, {
                headers: { 'Authorization': 'Bearer ' + token }
            });
            const getData = await getResponse.json();
            console.log('GET result:', getData);
            
            if (!getData.success) {
                alert('❌ GET failed: ' + getData.message);
                return;
            }
            
            console.log('Testing UPDATE...');
            const updateResponse = await fetch('/nlnapi/users.php?action=update', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
                body: JSON.stringify({
                    user_id: userId,
                    account_type: 'admin',
                    first_name: getData.user.first_name,
                    last_name: getData.user.last_name,
                    email: getData.user.email,
                    phone: getData.user.phone || '',
                    reason: 'Test from debug button'
                })
            });
            const updateData = await updateResponse.json();
            console.log('UPDATE result:', updateData);
            
            if (updateData.success) {
                alert('✅ API Test successful! Check the user in the list.');
                await fetchDashboardData();
            } else {
                alert('❌ UPDATE failed: ' + updateData.message);
            }
        } catch (error) {
            console.error('Test error:', error);
            alert('❌ Error: ' + error.message);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'approved': return '#10b981';
            case 'pending': return '#f59e0b';
            case 'rejected': return '#ef4444';
            case 'cancelled': return '#6b7280';
            default: return '#00843D';
        }
    };

    if (!dashboardData || !dashboardData.stats) {
        return (
            <div className="dashboard-error">
                <i className="fa-solid fa-circle-exclamation"></i>
                <h3>Unable to load dashboard</h3>
                <p>Please check your internet connection and try again.</p>
                <button onClick={fetchDashboardData} className="retry-btn">
                    <i className="fa-solid fa-rotate-right"></i> Retry
                </button>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <div className="admin-tabs">
                <button 
                    className={`admin-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
                    onClick={() => setActiveTab('dashboard')}
                >
                    <i className="fa-solid fa-chart-pie"></i> Dashboard
                </button>
                <button 
                    className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => {
                        setActiveTab('users');
                        if (users.length === 0) fetchDashboardData();
                    }}
                >
                    <i className="fa-solid fa-users"></i> User Management
                </button>
            </div>

            {activeTab === 'dashboard' ? (
                <>
                    <div className="executive-hero-section">
                        <div className="executive-hero-content">
                            <div className="executive-badge">
                                <i className="fa-solid fa-crown"></i>
                                <span>Executive Portal</span>
                            </div>
                            <h1 className="executive-title">Dashboard</h1>
                            <p className="executive-description">Welcome back, {user?.first_name || 'Administrator'} — here's your real-time club performance overview.</p>
                        </div>
                        <div className="executive-actions">
                            <button className="executive-refresh-btn" onClick={fetchDashboardData}>
                                <i className="fa-solid fa-rotate-right"></i> Sync Data
                            </button>
                        </div>
                    </div>

                    <div className="period-tabs-container">
                        <div className="period-tabs">
                            <button 
                                className={activePeriod === 'week' ? 'active' : ''}
                                onClick={() => setActivePeriod('week')}
                            >
                                <i className="fa-solid fa-calendar-week"></i>
                                <span>Weekly</span>
                            </button>
                            <button 
                                className={activePeriod === 'month' ? 'active' : ''}
                                onClick={() => setActivePeriod('month')}
                            >
                                <i className="fa-solid fa-calendar-month"></i>
                                <span>Monthly</span>
                            </button>
                            <button 
                                className={activePeriod === 'quarter' ? 'active' : ''}
                                onClick={() => setActivePeriod('quarter')}
                            >
                                <i className="fa-solid fa-chart-simple"></i>
                                <span>Quarterly</span>
                            </button>
                            <button 
                                className={activePeriod === 'year' ? 'active' : ''}
                                onClick={() => setActivePeriod('year')}
                            >
                                <i className="fa-solid fa-calendar-year"></i>
                                <span>Yearly</span>
                            </button>
                        </div>
                    </div>

                    <div className="stats-grid-modern">
                        <div className="stat-card-modern">
                            <div className="stat-icon-wrapper">
                                <div className="stat-icon-circle total">
                                    <i className="fa-solid fa-calendar-check"></i>
                                </div>
                            </div>
                            <div className="stat-content">
                                <span className="stat-value-modern">{dashboardData.stats.totalBookings.toLocaleString()}</span>
                                <span className="stat-label-modern">Total Bookings</span>
                            </div>
                            <div className="stat-trend positive">
                                <i className="fa-solid fa-arrow-up"></i>
                                <span>+12%</span>
                            </div>
                        </div>

                        <div className="stat-card-modern">
                            <div className="stat-icon-wrapper">
                                <div className="stat-icon-circle active">
                                    <i className="fa-solid fa-users"></i>
                                </div>
                            </div>
                            <div className="stat-content">
                                <span className="stat-value-modern">{dashboardData.stats.activeMembers.toLocaleString()}</span>
                                <span className="stat-label-modern">Active Members</span>
                            </div>
                            <div className="stat-trend positive">
                                <i className="fa-solid fa-arrow-up"></i>
                                <span>+8%</span>
                            </div>
                        </div>

                        <div className="stat-card-modern">
                            <div className="stat-icon-wrapper">
                                <div className="stat-icon-circle pending">
                                    <i className="fa-solid fa-clock"></i>
                                </div>
                            </div>
                            <div className="stat-content">
                                <span className="stat-value-modern">{dashboardData.stats.pendingApprovals}</span>
                                <span className="stat-label-modern">Pending Approvals</span>
                            </div>
                            <div className="stat-trend neutral">
                                <i className="fa-solid fa-minus"></i>
                                <span>Awaiting</span>
                            </div>
                        </div>

                        <div className="stat-card-modern">
                            <div className="stat-icon-wrapper">
                                <div className="stat-icon-circle utilization">
                                    <i className="fa-solid fa-chart-line"></i>
                                </div>
                            </div>
                            <div className="stat-content">
                                <span className="stat-value-modern">{dashboardData.stats.facilityUtilization}%</span>
                                <span className="stat-label-modern">Approval Rate</span>
                            </div>
                            <div className="stat-trend positive">
                                <i className="fa-solid fa-arrow-up"></i>
                                <span>+5%</span>
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-grid-modern">
                        <div className="dashboard-left-col">
                            <div className="dashboard-card-modern chart-card">
                                <div className="card-header-modern">
                                    <div className="header-title">
                                        <i className="fa-solid fa-chart-line"></i>
                                        <h3>Booking Trends</h3>
                                    </div>
                                    <div className="period-indicator">
                                        <i className="fa-regular fa-calendar"></i>
                                        <span>{activePeriod === 'week' ? 'Last 7 Days' : 
                                                 activePeriod === 'month' ? 'Last 30 Days' : 
                                                 activePeriod === 'quarter' ? 'Quarterly View' : 'Annual View'}</span>
                                    </div>
                                </div>
                                <div className="trends-chart-modern">
                                    {dashboardData.bookingTrends.length > 0 ? (
                                        dashboardData.bookingTrends.map((item, index) => {
                                            const maxValue = Math.max(...dashboardData.bookingTrends.map(t => t.total), 1);
                                            const height = (item.total / maxValue) * 140;
                                            return (
                                                <div key={index} className="trend-bar-modern">
                                                    <div className="bar-container">
                                                        <div className="bar-fill-modern" style={{ height: `${height}px` }}>
                                                            <span className="bar-value-modern">{item.total}</span>
                                                        </div>
                                                    </div>
                                                    <span className="bar-label-modern">{item.label || formatDate(item.date)}</span>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="no-data">No booking data available</div>
                                    )}
                                </div>
                            </div>

                            <div className="dashboard-card-modern facilities-card">
                                <div className="card-header-modern">
                                    <div className="header-title">
                                        <i className="fa-solid fa-trophy"></i>
                                        <h3>Most Requested Facilities</h3>
                                    </div>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </div>
                                <div className="facilities-list-modern">
                                    {dashboardData.popularFacilities.length > 0 ? (
                                        dashboardData.popularFacilities.map((facility, index) => (
                                            <div key={facility.facility_name} className="facility-item-modern">
                                                <div className="facility-rank-modern">{index + 1}</div>
                                                <div className="facility-info-modern">
                                                    <div className="facility-name-modern">{facility.facility_name}</div>
                                                    <div className="facility-progress">
                                                        <div className="facility-progress-bar">
                                                            <div className="facility-progress-fill" style={{ 
                                                                width: `${(facility.booking_count / dashboardData.popularFacilities[0].booking_count) * 100}%` 
                                                            }}></div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="facility-count-modern">
                                                    <span className="count-number">{facility.booking_count}</span>
                                                    <span className="count-label">bookings</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="no-data">No facility data available</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="dashboard-right-col">
                            <div className="dashboard-card-modern status-card">
                                <div className="card-header-modern">
                                    <div className="header-title">
                                        <i className="fa-solid fa-chart-pie"></i>
                                        <h3>Booking Status Overview</h3>
                                    </div>
                                </div>
                                <div className="status-grid-modern">
                                    {Object.entries(dashboardData.statusDistribution).map(([status, count]) => (
                                        count > 0 && (
                                            <div key={status} className="status-item-modern">
                                                <div className="status-info-modern">
                                                    <div className="status-badge" style={{ backgroundColor: getStatusColor(status) }}></div>
                                                    <span className="status-name">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                                                </div>
                                                <div className="status-value-modern">
                                                    <span className="status-count-modern">{count}</span>
                                                    <span className="status-percent">
                                                        {Math.round((count / dashboardData.stats.totalBookings) * 100)}%
                                                    </span>
                                                </div>
                                                <div className="status-progress">
                                                    <div className="status-progress-bar">
                                                        <div className="status-progress-fill" style={{ 
                                                            width: `${(count / dashboardData.stats.totalBookings) * 100}%`,
                                                            backgroundColor: getStatusColor(status)
                                                        }}></div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    ))}
                                </div>
                            </div>

                            <div className="dashboard-card-modern recent-card">
                                <div className="card-header-modern">
                                    <div className="header-title">
                                        <i className="fa-solid fa-clock"></i>
                                        <h3>Recent Activity</h3>
                                    </div>
                                    <button className="view-link" onClick={() => window.location.href = '/admin?tab=bookings'}>
                                        View All <i className="fa-solid fa-chevron-right"></i>
                                    </button>
                                </div>
                                <div className="timeline-list">
                                    {dashboardData.recentBookings.length > 0 ? (
                                        dashboardData.recentBookings.map((booking, idx) => (
                                            <div key={booking.id} className="timeline-item">
                                                <div className="timeline-marker" style={{ backgroundColor: getStatusColor(booking.status) }}></div>
                                                <div className="timeline-content">
                                                    <div className="timeline-title">{booking.facility_name || booking.location || 'Booking'}</div>
                                                    <div className="timeline-date">{formatDate(booking.created_at)}</div>
                                                </div>
                                                <div className={`timeline-status status-${booking.status}`}>
                                                    {booking.status || 'pending'}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="no-data">No recent bookings</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-card-modern inventory-card-full">
                        <div className="card-header-modern">
                            <div className="header-title">
                                <i className="fa-solid fa-toolbox"></i>
                                <h3>Equipment & Inventory Status</h3>
                            </div>
                            <i className="fa-solid fa-arrow-right"></i>
                        </div>
                        <div className="inventory-grid-modern">
                            {dashboardData.equipmentInventory.length > 0 ? (
                                dashboardData.equipmentInventory.slice(0, 4).map(item => (
                                    <div key={item.id} className="inventory-card-modern">
                                        <div className="inventory-icon">
                                            <i className="fa-solid fa-gear"></i>
                                        </div>
                                        <div className="inventory-details">
                                            <div className="inventory-name-modern">{item.name}</div>
                                            <div className="inventory-category-modern">{item.category}</div>
                                        </div>
                                        <div className={`inventory-stock-modern ${item.available > 0 ? 'in-stock' : 'out-stock'}`}>
                                            {item.available > 0 ? `${item.available} available` : 'Out of stock'}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-data">No equipment data available</div>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <div className="user-management-container">
                    <div style={{ 
                        marginBottom: 16, 
                        display: 'flex', 
                        gap: 12, 
                        flexWrap: 'wrap',
                        padding: '12px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6'
                    }}>
                        <button 
                            onClick={testDirectApiCall}
                            style={{
                                padding: '8px 16px',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '500'
                            }}
                        >
                            🧪 Test API (Linda Ogbu)
                        </button>
                        <button 
                            onClick={() => {
                                console.log('Current users:', users);
                                const linda = users.find(u => u.email === 'linda.ogbu@nlng.com');
                                console.log('Linda Ogbu:', linda);
                                alert(`Linda Ogbu found: ${linda ? '✅ Yes (ID: ' + linda.id + ')' : '❌ No'}`);
                            }}
                            style={{
                                padding: '8px 16px',
                                background: '#17a2b8',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '500'
                            }}
                        >
                            🔍 Find Linda Ogbu
                        </button>
                        <button 
                            onClick={() => {
                                const token = localStorage.getItem('nlngra_token');
                                console.log('Token:', token);
                                alert('Token: ' + (token ? token.substring(0, 20) + '...' : 'No token found'));
                            }}
                            style={{
                                padding: '8px 16px',
                                background: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '500'
                            }}
                        >
                            🔑 Show Token
                        </button>
                    </div>

                    <UserManagement
                        users={users}
                        onUpdateUser={handleUpdateUser}
                        onAssignRole={handleAssignRole}
                        onActivateUser={handleActivateUser}
                        onSuspendUser={handleSuspendUser}
                        onDeleteUser={handleDeleteUser}
                        addNotification={addNotification}
                        currentUserRole="admin"
                        canAssignRoles={true}
                    />
                </div>
            )}

            <style>{`
                .admin-tabs {
                    display: flex;
                    gap: 8px;
                    margin-bottom: 24px;
                    background: white;
                    padding: 8px;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                }
                .admin-tab {
                    padding: 12px 24px;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 500;
                    background: transparent;
                    color: #6b7280;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .admin-tab:hover {
                    background: #f3f4f6;
                    color: #1f2937;
                }
                .admin-tab.active {
                    background: #00843D;
                    color: white;
                    box-shadow: 0 2px 8px rgba(0,132,61,0.3);
                }
                .admin-tab i {
                    font-size: 16px;
                }
                .user-management-container {
                    background: white;
                    border-radius: 16px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                }
                .executive-hero-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    background: linear-gradient(135deg, #00843D 0%, #00a84d 100%);
                    padding: 24px 32px;
                    border-radius: 16px;
                    color: white;
                }
                .executive-hero-content {
                    flex: 1;
                }
                .executive-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(255,255,255,0.2);
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 500;
                }
                .executive-title {
                    margin: 8px 0 4px 0;
                    font-size: 28px;
                }
                .executive-description {
                    margin: 0;
                    opacity: 0.9;
                    font-size: 14px;
                }
                .executive-actions {
                    margin-left: 16px;
                }
                .executive-refresh-btn {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    padding: 10px 20px;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s;
                }
                .executive-refresh-btn:hover {
                    background: rgba(255,255,255,0.3);
                }
                .period-tabs-container {
                    margin-bottom: 24px;
                }
                .period-tabs {
                    display: flex;
                    gap: 4px;
                    background: white;
                    padding: 6px;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                    width: fit-content;
                }
                .period-tabs button {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 8px;
                    background: transparent;
                    color: #6b7280;
                    cursor: pointer;
                    font-size: 13px;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    transition: all 0.2s;
                }
                .period-tabs button:hover {
                    background: #f3f4f6;
                    color: #1f2937;
                }
                .period-tabs button.active {
                    background: #00843D;
                    color: white;
                    box-shadow: 0 2px 8px rgba(0,132,61,0.3);
                }
                .stats-grid-modern {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 16px;
                    margin-bottom: 24px;
                }
                .stat-card-modern {
                    background: white;
                    padding: 20px;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                .stat-icon-wrapper {
                    flex-shrink: 0;
                }
                .stat-icon-circle {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    color: white;
                }
                .stat-icon-circle.total { background: #00843D; }
                .stat-icon-circle.active { background: #2563eb; }
                .stat-icon-circle.pending { background: #f59e0b; }
                .stat-icon-circle.utilization { background: #8b5cf6; }
                .stat-content {
                    flex: 1;
                }
                .stat-value-modern {
                    display: block;
                    font-size: 24px;
                    font-weight: 700;
                    color: #1f2937;
                }
                .stat-label-modern {
                    display: block;
                    font-size: 13px;
                    color: #6b7280;
                }
                .stat-trend {
                    font-size: 12px;
                    font-weight: 500;
                    padding: 4px 8px;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }
                .stat-trend.positive { background: #d1fae5; color: #065f46; }
                .stat-trend.neutral { background: #f3f4f6; color: #6b7280; }
                .dashboard-grid-modern {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                    margin-bottom: 20px;
                }
                .dashboard-card-modern {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                    overflow: hidden;
                }
                .card-header-modern {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 20px;
                    border-bottom: 1px solid #f3f4f6;
                }
                .header-title {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .header-title i {
                    color: #00843D;
                    font-size: 18px;
                }
                .header-title h3 {
                    margin: 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #1f2937;
                }
                .trends-chart-modern {
                    padding: 20px;
                    display: flex;
                    align-items: flex-end;
                    gap: 8px;
                    height: 200px;
                }
                .trend-bar-modern {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    height: 100%;
                }
                .bar-container {
                    flex: 1;
                    display: flex;
                    align-items: flex-end;
                    width: 100%;
                }
                .bar-fill-modern {
                    width: 100%;
                    background: #00843D;
                    border-radius: 4px 4px 0 0;
                    min-height: 4px;
                    position: relative;
                    transition: height 0.3s ease;
                }
                .bar-value-modern {
                    position: absolute;
                    top: -18px;
                    left: 50%;
                    transform: translateX(-50%);
                    font-size: 10px;
                    color: #6b7280;
                    font-weight: 500;
                }
                .bar-label-modern {
                    font-size: 10px;
                    color: #6b7280;
                    margin-top: 8px;
                }
                .facilities-list-modern {
                    padding: 16px 20px;
                }
                .facility-item-modern {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 8px 0;
                    border-bottom: 1px solid #f3f4f6;
                }
                .facility-item-modern:last-child {
                    border-bottom: none;
                }
                .facility-rank-modern {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background: #f3f4f6;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 11px;
                    font-weight: 600;
                    color: #6b7280;
                }
                .facility-info-modern {
                    flex: 1;
                }
                .facility-name-modern {
                    font-size: 13px;
                    font-weight: 500;
                    color: #1f2937;
                }
                .facility-progress {
                    margin-top: 4px;
                }
                .facility-progress-bar {
                    height: 4px;
                    background: #f3f4f6;
                    border-radius: 2px;
                    overflow: hidden;
                }
                .facility-progress-fill {
                    height: 100%;
                    background: #00843D;
                    border-radius: 2px;
                    transition: width 0.3s ease;
                }
                .facility-count-modern {
                    text-align: right;
                }
                .count-number {
                    display: block;
                    font-size: 16px;
                    font-weight: 600;
                    color: #1f2937;
                }
                .count-label {
                    font-size: 10px;
                    color: #6b7280;
                }
                .status-grid-modern {
                    padding: 16px 20px;
                }
                .status-item-modern {
                    margin-bottom: 12px;
                }
                .status-item-modern:last-child {
                    margin-bottom: 0;
                }
                .status-info-modern {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 4px;
                }
                .status-badge {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                }
                .status-name {
                    font-size: 13px;
                    font-weight: 500;
                    color: #1f2937;
                }
                .status-value-modern {
                    display: flex;
                    justify-content: space-between;
                    font-size: 12px;
                    color: #6b7280;
                }
                .status-progress {
                    margin-top: 4px;
                }
                .status-progress-bar {
                    height: 4px;
                    background: #f3f4f6;
                    border-radius: 2px;
                    overflow: hidden;
                }
                .status-progress-fill {
                    height: 100%;
                    border-radius: 2px;
                    transition: width 0.3s ease;
                }
                .timeline-list {
                    padding: 16px 20px;
                }
                .timeline-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 8px 0;
                    border-bottom: 1px solid #f3f4f6;
                }
                .timeline-item:last-child {
                    border-bottom: none;
                }
                .timeline-marker {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    flex-shrink: 0;
                }
                .timeline-content {
                    flex: 1;
                }
                .timeline-title {
                    font-size: 13px;
                    font-weight: 500;
                    color: #1f2937;
                }
                .timeline-date {
                    font-size: 11px;
                    color: #6b7280;
                }
                .timeline-status {
                    font-size: 11px;
                    font-weight: 500;
                    padding: 2px 8px;
                    border-radius: 12px;
                }
                .timeline-status.status-pending { background: #fef3c7; color: #92400e; }
                .timeline-status.status-approved { background: #d1fae5; color: #065f46; }
                .timeline-status.status-rejected { background: #fecaca; color: #991b1b; }
                .timeline-status.status-cancelled { background: #f3f4f6; color: #6b7280; }
                .view-link {
                    background: none;
                    border: none;
                    color: #00843D;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }
                .view-link:hover {
                    text-decoration: underline;
                }
                .no-data {
                    padding: 20px;
                    text-align: center;
                    color: #6b7280;
                    font-size: 14px;
                }
                .inventory-grid-modern {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 16px;
                    padding: 16px 20px;
                }
                .inventory-card-modern {
                    background: #f9fafb;
                    padding: 16px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .inventory-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 8px;
                    background: #e5e7eb;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #6b7280;
                }
                .inventory-details {
                    flex: 1;
                }
                .inventory-name-modern {
                    font-size: 13px;
                    font-weight: 500;
                    color: #1f2937;
                }
                .inventory-category-modern {
                    font-size: 11px;
                    color: #6b7280;
                }
                .inventory-stock-modern {
                    font-size: 12px;
                    font-weight: 500;
                    padding: 2px 8px;
                    border-radius: 12px;
                }
                .inventory-stock-modern.in-stock { background: #d1fae5; color: #065f46; }
                .inventory-stock-modern.out-stock { background: #fecaca; color: #991b1b; }
                .dashboard-error {
                    text-align: center;
                    padding: 60px 20px;
                }
                .dashboard-error i {
                    font-size: 48px;
                    color: #ef4444;
                }
                .retry-btn {
                    margin-top: 16px;
                    padding: 10px 24px;
                    background: #00843D;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                }
                @media (max-width: 1024px) {
                    .stats-grid-modern {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    .dashboard-grid-modern {
                        grid-template-columns: 1fr;
                    }
                    .inventory-grid-modern {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
                @media (max-width: 768px) {
                    .stats-grid-modern {
                        grid-template-columns: 1fr;
                    }
                    .inventory-grid-modern {
                        grid-template-columns: 1fr;
                    }
                    .executive-hero-section {
                        flex-direction: column;
                        text-align: center;
                    }
                    .executive-actions {
                        margin-left: 0;
                        margin-top: 12px;
                    }
                    .period-tabs {
                        width: 100%;
                        justify-content: center;
                        flex-wrap: wrap;
                    }
                    .period-tabs button {
                        padding: 6px 12px;
                        font-size: 12px;
                    }
                    .admin-tabs {
                        flex-wrap: wrap;
                    }
                    .admin-tab {
                        flex: 1;
                        justify-content: center;
                        padding: 10px 16px;
                        font-size: 13px;
                    }
                }
            `}</style>
        </div>
    );
};

export default AdminDashboard;